import { useCallback, useMemo, useState } from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import type { WrapUpField } from "@/hooks/useEventsPipeline";
import type { EventBoardHandlers, EventBoardPending } from "@/hooks/useEventBoardActions";
import { STAGE_INFO, fmtMoney, type BoardRow } from "@/lib/event-board";
import { sortRows } from "@/lib/event-filters";
import { CLOSED_STAGES, OPEN_STAGES, type EventStage } from "@/lib/event-pipeline";
import { EventPipelineCard } from "./EventPipelineCard";
import { NewBadge, StageInfo } from "./StageInfo";

const PAGE = 20;
const BY_DATE = { key: "date", dir: 1 } as const;

interface EventsPipelineViewProps {
  /** The filtered rows. */
  rows: BoardRow[];
  /** The stages picked in Event status: one column each, in board order. */
  stages: EventStage[];
  handlers: EventBoardHandlers;
  pending: EventBoardPending;
  wrapUpUnavailable: boolean;
}

interface ColumnProps extends Omit<EventsPipelineViewProps, "rows" | "stages"> {
  stage: EventStage;
  rows: BoardRow[];
  visible: number;
  onShowMore: (stage: EventStage) => void;
}

function pendingFieldFor(pending: string | null, id: string): WrapUpField | null {
  if (!pending?.startsWith(`${id}:`)) return null;
  return pending.slice(id.length + 1) as WrapUpField;
}

function StageColumn({ stage, rows, visible, onShowMore, handlers, pending, wrapUpUnavailable }: ColumnProps) {
  const info = STAGE_INFO[stage];
  const guests = rows.reduce((s, r) => s + r.guests, 0);
  const total = Math.round(rows.reduce((s, r) => s + r.total, 0) * 100) / 100;
  const more = rows.length - visible;
  return (
    <section
      aria-label={info.label}
      className="flex min-h-[220px] flex-col gap-2.5 rounded-[10px] border-t-[3px] bg-muted p-2.5"
      style={{ borderTopColor: info.color }}
    >
      <div className="grid gap-px px-1 py-0.5">
        <div className="flex items-center gap-[5px]">
          <h3 className="text-sm font-bold">{info.label}</h3>
          {info.isNew && <NewBadge />}
          <StageInfo stage={stage} />
        </div>
        <div className="text-[12.5px] tabular-nums text-muted-foreground">
          <b className="text-foreground">{rows.length}</b> · {guests} guests · {fmtMoney(total)}
        </div>
      </div>
      {rows.length === 0 ? (
        <p className="px-1 py-2 text-[13px] text-muted-foreground">Nothing here with these filters.</p>
      ) : (
        rows
          .slice(0, visible)
          .map((row) => (
            <EventPipelineCard
              key={row.id}
              row={row}
              handlers={handlers}
              pendingField={pendingFieldFor(pending.wrapUp, row.id)}
              donePending={pending.done === row.id}
              addQuotePending={pending.addQuote === row.id}
              wrapUpUnavailable={wrapUpUnavailable}
            />
          ))
      )}
      {more > 0 && (
        <button
          type="button"
          onClick={() => onShowMore(stage)}
          className="rounded-md py-1 text-[13px] font-semibold text-muted-foreground hover:bg-card hover:text-foreground"
        >
          Show {Math.min(PAGE, more)} more
        </button>
      )}
    </section>
  );
}

/** /admin/events Pipeline (#1557): a column per picked stage, cards by date. No drag and drop. */
export function EventsPipelineView({ rows, stages, handlers, pending, wrapUpUnavailable }: EventsPipelineViewProps) {
  const [visible, setVisible] = useState<Partial<Record<EventStage, number>>>({});

  const columns = useMemo(() => [...OPEN_STAGES, ...CLOSED_STAGES].filter((s) => stages.includes(s)), [stages]);
  const byStage = useMemo(() => {
    const m = new Map<EventStage, BoardRow[]>();
    for (const r of rows) m.set(r.stage, [...(m.get(r.stage) ?? []), r]);
    for (const [s, list] of m) m.set(s, sortRows(list, BY_DATE));
    return m;
  }, [rows]);

  const showMore = useCallback((s: EventStage) => setVisible((v) => ({ ...v, [s]: (v[s] ?? PAGE) + PAGE })), []);

  if (columns.length === 0) {
    return (
      <p className="px-1 py-2 text-[13px] text-muted-foreground">No stages picked. Choose some under Event status.</p>
    );
  }

  return (
    // Radix wraps the content in a display:table div, which would size the 1fr columns to their
    // widest text; as a block, the columns fill the width and scroll once they reach 220px.
    <ScrollArea className="w-full [&_[data-radix-scroll-area-viewport]>div]:!block">
      <div className="grid min-w-min auto-cols-[minmax(220px,1fr)] grid-flow-col gap-3 pb-2">
        {columns.map((s) => (
          <StageColumn
            key={s}
            stage={s}
            rows={byStage.get(s) ?? []}
            visible={visible[s] ?? PAGE}
            onShowMore={showMore}
            handlers={handlers}
            pending={pending}
            wrapUpUnavailable={wrapUpUnavailable}
          />
        ))}
      </div>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
}
